import ActionPanel from '@/components/ActionPanel';
import PageContent from '@/components/PageContent';
import { AdvanceShareStyleVersion, AdvanceShareStyleVersionStatusEnum } from '@/models/advance-share-style-version.model';
import { BaseErrorModel, BaseResponseModel } from '@/models/core.model';
import { publishAdvanceShareStyleVersion, upsertAdvanceShareStyleVersion } from '@/services/advance-share-style-version.service';
import { toastErrorMessage, toastSuccessMessage } from '@/utils/toasterMessage';
import { AxiosError } from 'axios';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Message, PanelGroup, useToaster } from 'rsuite';
import GenerateAdvanceShareActionModal from './generate-advance-share-action-modal';



interface AdvanceShareStyleVersionActionsProps{
  formObject?: AdvanceShareStyleVersion;
  fetchData: () => void;    
};

const AdvanceShareStyleVersionActions: React.FC<AdvanceShareStyleVersionActionsProps> = ({formObject, fetchData}) => {
  const [loadingPublishVersion, setLoadingPublishVersion] = useState(false);
  const [loadingDuplicateVersion, setLoadingDuplicateVersion] = useState(false);
  const [openGenerateModal, setOpenGenerateModal] = useState(false);

  const nav = useNavigate();
  const toaster = useToaster();

  const handleClickPublishStyleVersion = () => {
    if(!formObject) return;

    const handleYesClick = async () => {
      setLoadingPublishVersion(true);
      try {
        const result: BaseResponseModel<null> = await publishAdvanceShareStyleVersion(formObject.id);
        if (result.success) {
          toastSuccessMessage(`Style Version Successfully Published.`);
          fetchData();
        }
      } catch (err) {
        const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
        toastErrorMessage(`Error in publishing Style Version:${errorModel?.errorMessage}`);
      }
      finally {
        setLoadingPublishVersion(false);
        toaster.clear(); 
      }
    };

    const handleNoClick = () => {
      toaster.clear(); 
    };

    toaster.push(
      <Message type="info" closable>
        <p style={{marginBottom:10}}>Are you sure you want to <strong>Publish Style Version</strong>?</p>
        <div>
          <Button appearance="primary" onClick={handleYesClick}>Yes</Button>
          <Button appearance="subtle" onClick={handleNoClick} style={{ marginLeft:10}}>No</Button>
        </div>
      </Message>,
      { placement: 'topCenter', duration: 0 }
    );

  };
  const handleClickDuplicateStyleVersion = () => {
    if(!formObject) return;

    const handleYesClick = async () => {
      setLoadingDuplicateVersion(true);
      try {
        const result: BaseResponseModel<AdvanceShareStyleVersion> = await upsertAdvanceShareStyleVersion({...formObject, id: undefined});
        if (result.success) {
          toastSuccessMessage(`Style Version Successfully Duplicated.`);
          fetchData();
        }
      } catch (err) {
        const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
        toastErrorMessage(`Error in duplicating Style Version:${errorModel?.errorMessage}`);
      }
      finally {
        setLoadingDuplicateVersion(false);
        toaster.clear(); 
      }
    };

    const handleNoClick = () => {
      toaster.clear(); 
    };

    toaster.push(
      <Message type="info" closable>
        <p style={{marginBottom:10}}>Are you sure you want to <strong>Duplicate Style Version</strong>?</p>
        <div>
          <Button appearance="primary" onClick={handleYesClick}>Yes</Button>
          <Button appearance="subtle" onClick={handleNoClick} style={{ marginLeft:10}}>No</Button>
        </div>
      </Message>,
      { placement: 'topCenter', duration: 0 }
    );

  };

  return (
      <PageContent>
          <PanelGroup>
              <ActionPanel
                title='Publish Style Version'
                description='This action will publish the style version and make it available for users. Only style versions in draft status can be published. Make sure to review the style version details before publishing. Once published, the style version cannot be edited. If you need to make changes, you will have to create a new style version.'
                onClick={handleClickPublishStyleVersion}
                loading={loadingPublishVersion}
                disabled={formObject?.status != AdvanceShareStyleVersionStatusEnum.Draft}
              />
              <ActionPanel
                title='Duplicate Style Version'
                description='This action will create a duplicate of the current style version. This can be useful if you want to create a new version based on the current one with some modifications. The duplicated version will be in draft status, and you can edit it as needed before publishing.'
                onClick={handleClickDuplicateStyleVersion}
                loading={loadingDuplicateVersion}
              />
              <ActionPanel
                title={'Generate'}
                description='This action generate a preview advance share base on current version.'
                onClick={() => setOpenGenerateModal(true)}
              />
          </PanelGroup>
          
          {formObject?.id && <GenerateAdvanceShareActionModal
            styleVersion={formObject}
            open ={openGenerateModal}
            onClose={()=> {
              setOpenGenerateModal(false);
            }}
          />}         
      </PageContent>
  );
};

export default AdvanceShareStyleVersionActions;
